import type { Campaign as GraphQLCampaign } from '@/routes/campaigns/hooks/use-campaigns';
import type { Campaign, CampaignChannel, CampaignScheduling } from '../types';
import { CampaignType } from '../types';

/**
 * Aggregated metrics for all campaigns
 * @interface CampaignMetrics
 * @property {number} messagesSent - Total number of messages sent across all campaigns
 * @property {number} clicks - Total number of clicks across all campaigns
 * @property {number} orders - Total number of orders generated from all campaigns
 * @property {number} revenue - Total revenue generated from all campaigns
 * @property {number} ctr - Click-through rate as a percentage
 * @property {number} cvr - Conversion rate as a percentage
 * @property {number} epm - Earnings per message (revenue / messages sent)
 */
interface CampaignMetrics {
  messagesSent: number;
  clicks: number;
  orders: number;
  revenue: number;
  ctr: number;
  cvr: number;
  epm: number;
}

/**
 * Metrics broken down by channel (SMS, Email, Push)
 * @interface ChannelMetrics
 * @property {Record<CampaignChannel, number>} messages - Messages sent per channel
 * @property {Record<CampaignChannel, number>} clicks - Clicks per channel
 * @property {Record<CampaignChannel, number>} orders - Orders per channel
 * @property {Record<CampaignChannel, number>} revenue - Revenue per channel
 */
interface ChannelMetrics {
  messages: Record<CampaignChannel, number>;
  clicks: Record<CampaignChannel, number>;
  orders: Record<CampaignChannel, number>;
  revenue: Record<CampaignChannel, number>;
}

/**
 * Complete transformed campaign data structure
 * @interface TransformedCampaignData
 * @property {Campaign[]} campaigns - Array of transformed campaign data
 * @property {CampaignMetrics} metrics - Aggregated metrics across all campaigns
 * @property {ChannelMetrics} channelBreakdowns - Metrics broken down by channel
 */
export interface TransformedCampaignData {
  campaigns: Campaign[];
  metrics: CampaignMetrics;
  channelBreakdowns: ChannelMetrics;
}

/**
 * Calculates engagement and revenue rates from raw metrics
 * @param {number} messagesSent - Total messages sent
 * @param {number} clicks - Total clicks
 * @param {number} orders - Total orders
 * @param {number} revenue - Total revenue
 * @returns {Object} Calculated rates (ctr, cvr, epm)
 */
const calculateRates = (messagesSent: number, clicks: number, orders: number, revenue: number) => ({
  ctr: messagesSent > 0 ? (clicks / messagesSent) * 100 : 0,
  cvr: clicks > 0 ? (orders / clicks) * 100 : 0,
  epm: messagesSent > 0 ? revenue / messagesSent : 0
});

/**
 * Initializes an empty channel metrics object with zero values
 * @returns {ChannelMetrics} Empty channel metrics structure
 */
const initializeChannelMetrics = (): ChannelMetrics => ({
  messages: { sms: 0, email: 0, push: 0 },
  clicks: { sms: 0, email: 0, push: 0 },
  orders: { sms: 0, email: 0, push: 0 },
  revenue: { sms: 0, email: 0, push: 0 }
});

/**
 * Transforms raw GraphQL campaign data into a format suitable for UI consumption
 * This includes:
 * - Converting GraphQL enums to lowercase UI-friendly strings
 * - Aggregating metrics across all campaigns
 * - Calculating rates (CTR, CVR, EPM)
 * - Breaking down metrics by channel
 *
 * @param {GraphQLCampaign[]} campaigns - Raw campaign data from GraphQL API
 * @returns {TransformedCampaignData} Transformed and aggregated campaign data
 *
 * @example
 * const { campaigns, metrics, channelBreakdowns } = transformCampaignData(rawCampaigns)
 * console.log(metrics.ctr) // Click-through rate as percentage
 * console.log(channelBreakdowns.messages.sms) // Total SMS messages sent
 */
export const transformCampaignData = (campaigns: GraphQLCampaign[]): TransformedCampaignData => {
  // Transform individual campaigns
  const transformedCampaigns: Campaign[] = campaigns.map((campaign) => ({
    campaignId: campaign.campaignId,
    name: campaign.name,
    type: campaign.variants.length === 1 ? CampaignType.BLAST : CampaignType.SPLIT_SEND,
    status: campaign.status.toLowerCase() as Campaign['status'],
    merchantId: campaign.merchantId,
    createdAt: campaign.createdAt,
    generatedAt: campaign.generatedAt ?? undefined,
    segments: campaign.segments,
    channels: [
      ...new Set(
        campaign.variants.map((variant) => variant.channel.toLowerCase() as CampaignChannel)
      )
    ], // dedupe channels
    scheduled: campaign.scheduled as CampaignScheduling,
    variants: campaign.variants,
    metrics: {
      opened: campaign.metrics.opened,
      sent: campaign.metrics.sent,
      clicked: campaign.metrics.clicked,
      ordered: campaign.metrics.ordered,
      earned: campaign.metrics.earned
    }
  }));

  // Calculate total metrics across all campaigns
  const totalMetrics = transformedCampaigns.reduce(
    (acc, campaign) => ({
      messagesSent: acc.messagesSent + campaign.metrics.sent,
      clicks: acc.clicks + campaign.metrics.clicked,
      orders: acc.orders + campaign.metrics.ordered,
      revenue: acc.revenue + campaign.metrics.earned
    }),
    { messagesSent: 0, clicks: 0, orders: 0, revenue: 0 }
  );

  // Calculate metrics broken down by channel
  const channelBreakdowns = transformedCampaigns.reduce((acc, campaign) => {
    campaign.channels.forEach((channel) => {
      acc.messages[channel] += campaign.metrics.sent;
      acc.clicks[channel] += campaign.metrics.clicked;
      acc.orders[channel] += campaign.metrics.ordered;
      acc.revenue[channel] += campaign.metrics.earned;
    });
    return acc;
  }, initializeChannelMetrics());

  return {
    campaigns: transformedCampaigns,
    metrics: {
      ...totalMetrics,
      ...calculateRates(
        totalMetrics.messagesSent,
        totalMetrics.clicks,
        totalMetrics.orders,
        totalMetrics.revenue
      )
    },
    channelBreakdowns
  };
};
